import {
    activityLabelSchema,
    activityNoteSchema,
    breastSideSchema,
    calendarDateSchema,
    durationSecondsSchema,
    entityIdSchema,
    feedingMethodSchema,
    gramsSchema,
    ianaTimeZoneSchema,
    milkTypeSchema,
    millilitresSchema,
    nappyTypeSchema,
    pumpTotalSchema,
    sleepKindSchema,
    stoolColourSchema,
    stoolTextureSchema,
    utcInstantSchema,
} from '@baby-tracker/validation';
import { z } from 'zod';

import { successEnvelope } from './envelope.js';
import { familyPath } from './families.js';
import { API_BASE_PATH } from './routes.js';

const activeSideSchema = z.enum(['left', 'right']);

export function feedingsPath(familyId: string): string {
    return `${familyPath(familyId)}/feedings`;
}
export function feedingPath(feedingId: string): string {
    return `${API_BASE_PATH}/feedings/${feedingId}`;
}
export function feedingStopPath(feedingId: string): string {
    return `${feedingPath(feedingId)}/stop`;
}
export function feedingSidePath(feedingId: string): string {
    return `${feedingPath(feedingId)}/side`;
}

export function pumpingPath(familyId: string): string {
    return `${familyPath(familyId)}/pumping`;
}
export function pumpingSessionPath(sessionId: string): string {
    return `${API_BASE_PATH}/pumping/${sessionId}`;
}
export function pumpingStopPath(sessionId: string): string {
    return `${pumpingSessionPath(sessionId)}/stop`;
}

export function sleepPath(familyId: string): string {
    return `${familyPath(familyId)}/sleep`;
}
export function sleepSessionPath(sessionId: string): string {
    return `${API_BASE_PATH}/sleep/${sessionId}`;
}
export function sleepStopPath(sessionId: string): string {
    return `${sleepSessionPath(sessionId)}/stop`;
}

export function nappiesPath(familyId: string): string {
    return `${familyPath(familyId)}/nappies`;
}
export function nappyPath(nappyId: string): string {
    return `${API_BASE_PATH}/nappies/${nappyId}`;
}

export function summaryPath(familyId: string): string {
    return `${familyPath(familyId)}/summary`;
}
export function daysPath(familyId: string): string {
    return `${familyPath(familyId)}/days`;
}
export function timelinePath(familyId: string): string {
    return `${familyPath(familyId)}/timeline`;
}

export const activityTypeSchema = z.enum(['feeding', 'pumping', 'sleep', 'nappy']);
export type ActivityType = z.infer<typeof activityTypeSchema>;

export const feedingSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    method: feedingMethodSchema,
    startAt: utcInstantSchema,
    endAt: utcInstantSchema.nullable(),
    breastSide: breastSideSchema.nullable(),
    leftDurationS: durationSecondsSchema.nullable(),
    rightDurationS: durationSecondsSchema.nullable(),
    activeSide: activeSideSchema.nullable(),
    activeSideStartedAt: utcInstantSchema.nullable(),
    milkType: milkTypeSchema.nullable(),
    volumeMl: millilitresSchema.nullable(),
    formulaName: z.string().nullable(),
    foodDescription: z.string().nullable(),
    amountG: gramsSchema.nullable(),
    reaction: z.string().nullable(),
    linkedFeedingId: entityIdSchema.nullable(),
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});

export type Feeding = z.infer<typeof feedingSchema>;

const feedingShared = {
    childId: entityIdSchema,
    startAt: utcInstantSchema.optional(),
    notes: activityNoteSchema.nullish(),
    linkedFeedingId: entityIdSchema.nullish(),
};

export const createFeedingInputSchema = z.discriminatedUnion('method', [
    z.object({
        ...feedingShared,
        method: z.literal('breast'),
        endAt: utcInstantSchema.nullish(),
        breastSide: breastSideSchema.nullish(),
        leftDurationS: durationSecondsSchema.nullish(),
        rightDurationS: durationSecondsSchema.nullish(),
        activeSide: activeSideSchema.optional(),
    }),
    z.object({
        ...feedingShared,
        method: z.literal('bottle'),
        milkType: milkTypeSchema,
        volumeMl: millilitresSchema,
        formulaName: activityLabelSchema.nullish(),
    }),
    z.object({
        ...feedingShared,
        method: z.literal('solid'),
        foodDescription: activityLabelSchema,
        amountG: gramsSchema.nullish(),
        reaction: activityLabelSchema.nullish(),
    }),
]);

export type CreateFeedingInput = z.infer<typeof createFeedingInputSchema>;

export const updateFeedingInputSchema = z
    .object({
        startAt: utcInstantSchema.optional(),
        breastSide: breastSideSchema.nullish(),
        leftDurationS: durationSecondsSchema.nullish(),
        rightDurationS: durationSecondsSchema.nullish(),
        milkType: milkTypeSchema.nullish(),
        volumeMl: millilitresSchema.nullish(),
        formulaName: activityLabelSchema.nullish(),
        foodDescription: activityLabelSchema.nullish(),
        amountG: gramsSchema.nullish(),
        reaction: activityLabelSchema.nullish(),
        linkedFeedingId: entityIdSchema.nullish(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
        message: 'At least one field is required',
    });

export type UpdateFeedingInput = z.infer<typeof updateFeedingInputSchema>;

export const stopTimerInputSchema = z.object({
    endAt: utcInstantSchema.optional(),
});

export type StopTimerInput = z.infer<typeof stopTimerInputSchema>;

export const switchSideInputSchema = z.object({
    side: activeSideSchema,
});

export type SwitchSideInput = z.infer<typeof switchSideInputSchema>;

export const pumpingSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    userId: entityIdSchema,
    childId: entityIdSchema.nullable(),
    startAt: utcInstantSchema,
    endAt: utcInstantSchema.nullable(),
    leftMl: millilitresSchema.nullable(),
    rightMl: millilitresSchema.nullable(),
    totalMl: pumpTotalSchema.nullable(),
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});

export type PumpingSession = z.infer<typeof pumpingSchema>;

export const createPumpingInputSchema = z.object({
    childId: entityIdSchema.nullish(),
    startAt: utcInstantSchema.optional(),
    endAt: utcInstantSchema.nullish(),
    leftMl: millilitresSchema.nullish(),
    rightMl: millilitresSchema.nullish(),
    totalMl: pumpTotalSchema.nullish(),
    notes: activityNoteSchema.nullish(),
});

export type CreatePumpingInput = z.infer<typeof createPumpingInputSchema>;

export const updatePumpingInputSchema = z
    .object({
        childId: entityIdSchema.nullish(),
        startAt: utcInstantSchema.optional(),
        leftMl: millilitresSchema.nullish(),
        rightMl: millilitresSchema.nullish(),
        totalMl: pumpTotalSchema.nullish(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
        message: 'At least one field is required',
    });

export type UpdatePumpingInput = z.infer<typeof updatePumpingInputSchema>;

export const stopPumpingInputSchema = stopTimerInputSchema.extend({
    leftMl: millilitresSchema.nullish(),
    rightMl: millilitresSchema.nullish(),
    totalMl: pumpTotalSchema.nullish(),
});

export type StopPumpingInput = z.infer<typeof stopPumpingInputSchema>;

export const sleepSchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    startAt: utcInstantSchema,
    endAt: utcInstantSchema.nullable(),
    kind: sleepKindSchema,
    location: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});

export type SleepSession = z.infer<typeof sleepSchema>;

export const createSleepInputSchema = z.object({
    childId: entityIdSchema,
    startAt: utcInstantSchema.optional(),
    endAt: utcInstantSchema.nullish(),
    kind: sleepKindSchema.optional(),
    location: activityLabelSchema.nullish(),
    notes: activityNoteSchema.nullish(),
});

export type CreateSleepInput = z.infer<typeof createSleepInputSchema>;

export const updateSleepInputSchema = z
    .object({
        startAt: utcInstantSchema.optional(),
        kind: sleepKindSchema.optional(),
        location: activityLabelSchema.nullish(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
        message: 'At least one field is required',
    });

export type UpdateSleepInput = z.infer<typeof updateSleepInputSchema>;

export const nappySchema = z.object({
    id: entityIdSchema,
    familyId: entityIdSchema,
    childId: entityIdSchema,
    changedAt: utcInstantSchema,
    kind: nappyTypeSchema,
    stoolColour: stoolColourSchema.nullable(),
    stoolTexture: stoolTextureSchema.nullable(),
    hasRash: z.boolean(),
    notes: z.string().nullable(),
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
});

export type NappyChange = z.infer<typeof nappySchema>;

export const createNappyInputSchema = z
    .object({
        childId: entityIdSchema,
        changedAt: utcInstantSchema.optional(),
        kind: nappyTypeSchema,
        stoolColour: stoolColourSchema.nullish(),
        stoolTexture: stoolTextureSchema.nullish(),
        hasRash: z.boolean().optional(),
        notes: activityNoteSchema.nullish(),
    })
    .refine(
        (value) =>
            value.kind === 'dirty' ||
            value.kind === 'mixed' ||
            ((value.stoolColour === undefined || value.stoolColour === null) &&
                (value.stoolTexture === undefined || value.stoolTexture === null)),
        { message: 'Stool details are only for dirty or mixed nappies' },
    );

export type CreateNappyInput = z.infer<typeof createNappyInputSchema>;

export const updateNappyInputSchema = z
    .object({
        changedAt: utcInstantSchema.optional(),
        kind: nappyTypeSchema.optional(),
        stoolColour: stoolColourSchema.nullish(),
        stoolTexture: stoolTextureSchema.nullish(),
        hasRash: z.boolean().optional(),
        notes: activityNoteSchema.nullish(),
    })
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
        message: 'At least one field is required',
    });

export type UpdateNappyInput = z.infer<typeof updateNappyInputSchema>;

export const nappyCountsSchema = z.object({
    wet: z.number().int().nonnegative(),
    dirty: z.number().int().nonnegative(),
    mixed: z.number().int().nonnegative(),
    dry: z.number().int().nonnegative(),
});

const aggregateSchema = z.number().int().nonnegative();

export const childDaySummarySchema = z.object({
    childId: entityIdSchema,
    breastSeconds: aggregateSchema,
    bottleMl: aggregateSchema,
    solidCount: aggregateSchema,
    sleepSeconds: aggregateSchema,
    nappies: nappyCountsSchema,
    openFeeding: feedingSchema.nullable(),
    openSleep: sleepSchema.nullable(),
});

export type ChildDaySummary = z.infer<typeof childDaySummarySchema>;

export const daySummarySchema = z.object({
    date: calendarDateSchema,
    timeZone: ianaTimeZoneSchema,
    from: utcInstantSchema,
    to: utcInstantSchema,
    children: z.array(childDaySummarySchema),
    pumpingMl: aggregateSchema,
    openPump: pumpingSchema.nullable(),
});

export type DaySummary = z.infer<typeof daySummarySchema>;

export const timelineItemSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('feeding'), at: utcInstantSchema, feeding: feedingSchema }),
    z.object({ type: z.literal('pumping'), at: utcInstantSchema, pumping: pumpingSchema }),
    z.object({ type: z.literal('sleep'), at: utcInstantSchema, sleep: sleepSchema }),
    z.object({ type: z.literal('nappy'), at: utcInstantSchema, nappy: nappySchema }),
]);

export type TimelineItem = z.infer<typeof timelineItemSchema>;

export const timelineSchema = z.array(timelineItemSchema);

export const timeRangeQuerySchema = z
    .object({
        from: utcInstantSchema,
        to: utcInstantSchema,
    })
    .refine((value) => value.from < value.to, { message: 'from must be before to' });

export const timelineQuerySchema = z
    .object({
        from: utcInstantSchema,
        to: utcInstantSchema,
        childId: entityIdSchema.optional(),
        type: activityTypeSchema.optional(),
    })
    .refine((value) => value.from < value.to, { message: 'from must be before to' });

export const summaryQuerySchema = z.object({
    date: calendarDateSchema,
});

export const dayRangeQuerySchema = z
    .object({
        from: calendarDateSchema,
        to: calendarDateSchema,
    })
    .refine((value) => value.from <= value.to, { message: 'from must be on or before to' });

export const dayRollupChildSchema = z.object({
    childId: entityIdSchema,
    breastSeconds: aggregateSchema,
    bottleMl: aggregateSchema,
    solidCount: aggregateSchema,
    sleepSeconds: aggregateSchema,
    nappyCount: aggregateSchema,
});

export const dayRollupSchema = z.object({
    date: calendarDateSchema,
    children: z.array(dayRollupChildSchema),
    pumpingMl: aggregateSchema,
});

export type DayRollup = z.infer<typeof dayRollupSchema>;

export const dayRollupListSchema = z.array(dayRollupSchema);

export const feedingResponseSchema = successEnvelope(feedingSchema);
export const feedingListResponseSchema = successEnvelope(z.array(feedingSchema));
export const pumpingResponseSchema = successEnvelope(pumpingSchema);
export const pumpingListResponseSchema = successEnvelope(z.array(pumpingSchema));
export const sleepResponseSchema = successEnvelope(sleepSchema);
export const sleepListResponseSchema = successEnvelope(z.array(sleepSchema));
export const nappyResponseSchema = successEnvelope(nappySchema);
export const nappyListResponseSchema = successEnvelope(z.array(nappySchema));
export const daySummaryResponseSchema = successEnvelope(daySummarySchema);
export const dayRollupListResponseSchema = successEnvelope(dayRollupListSchema);
export const timelineResponseSchema = successEnvelope(timelineSchema);
